/**
 * Danışan dosyasının okul bölümü: öğretmen ataması ve öğretmen notu.
 *
 * ATAMA ONAMA BAĞLI. Kuralı veritabanı uyguluyor (sql/0021); burada
 * onam yoksa düğme kapalı tutuluyor ve sebebi yazılıyor. Kuralın iki
 * yerde durması değil bu: veritabanı engelliyor, ekran engellenmeyi
 * ÖNCEDEN söylüyor. İkisi çelişirse kazanan veritabanı.
 *
 * ÖĞRETMEN NOTU (çocuk, öğretmen) çiftine yazılıyor. Sınıf
 * öğretmenine yazılan not rehber öğretmene gitmiyor; ekranda kime
 * yazıldığı ve öğretmenin bunu göreceği açıkça duruyor.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  OGRETMEN_ADRESI,
  OGRETMEN_TURU_ADI,
  ogretmenApi,
  type DosyaOgretmeni,
  type OgretmenSatiri,
} from '../api'

const gun = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('tr-TR', { dateStyle: 'medium' }) : '—'

export function DosyaOkul({
  danisanId,
  yazabilir,
  okulOnami,
  onDegisti,
}: {
  danisanId: string
  yazabilir: boolean
  /** Dosyadaki okul paylaşım onamı — yoksa atama yapılamıyor */
  okulOnami: boolean
  onDegisti: () => void
}) {
  const [liste, setListe] = useState<DosyaOgretmeni[] | null>(null)
  const [ataAcik, setAtaAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await ogretmenApi.danisanin(danisanId)
      setListe(r.ogretmenler)
    } catch {
      setHata('Öğretmen listesi alınamadı.')
      setListe([])
    }
  }, [danisanId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  return (
    <div className="kart">
      <h2 style={{ margin: '0 0 4px' }}>Okul tarafı</h2>
      <p className="alt" style={{ marginBottom: 12 }}>
        Atanan öğretmen, öğrencinin adını ve kendisine yazdığınız notu görüyor; gözlem formu
        doldurabiliyor. Dosya, rapor, ölçüm sonucu ve veli bilgisi okula açılmıyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {!okulOnami && (
        <div className="bilgi">
          <strong>Okulla paylaşım onamı yok.</strong> Öğretmene bir çocuğu açmak, okula “bu
          çocuk destek alıyor” bilgisini vermek demek; veli portalı onamı bunu kapsamıyor.
          Onamı Onamlar bölümünden işaretlemeden atama yapılamıyor.
        </div>
      )}

      {liste === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : liste.length === 0 ? (
        <p className="alt">Bu dosyada atanmış öğretmen yok.</p>
      ) : (
        liste.map((o) => (
          <AtamaSatiri
            key={o.atamaId}
            o={o}
            yazabilir={yazabilir}
            onDegisti={() => {
              void yukle()
              onDegisti()
            }}
          />
        ))
      )}

      {yazabilir && (
        <div className="dugmeler">
          <button onClick={() => setAtaAcik((x) => !x)} disabled={!okulOnami}>
            {ataAcik ? 'Vazgeç' : 'Öğretmen ata'}
          </button>
        </div>
      )}

      {ataAcik && okulOnami && (
        <OgretmenSec
          danisanId={danisanId}
          atanmis={new Set(liste?.map((x) => x.ogretmenId) ?? [])}
          onAtandi={() => {
            setAtaAcik(false)
            void yukle()
            /*
             * ÜST BİLEŞENE DE HABER VERİLİYOR.
             *
             * Eskiden yalnızca bu bölümün kendi listesi yenileniyordu:
             * öğretmen atanıyor, "Okul tarafı"nda görünüyor, ama
             * aşağıdaki "Gözlem formları" bölümü haberdar olmuyor ve
             * "Atanmış öğretmen yok" demeye devam ediyordu.
             *
             * Atamayı KALDIRMAK `onDegisti()` çağırıyordu, ATAMAK
             * çağırmıyordu — yani sinyal vardı ama bir yönü eksikti.
             * Uygulayıcı atamayı kaldırıp yeniden attığında da
             * düzelmemesinin sebebi tam olarak buydu.
             */
            onDegisti()
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function AtamaSatiri({
  o,
  yazabilir,
  onDegisti,
}: {
  o: DosyaOgretmeni
  yazabilir: boolean
  onDegisti: () => void
}) {
  const [notAcik, setNotAcik] = useState(false)
  const [not, setNot] = useState(o.not ?? '')
  const [bekliyor, setBekliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const kaydet = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      await ogretmenApi.notKaydet(o.atamaId, not)
      setNotAcik(false)
      onDegisti()
    } catch {
      setHata('Not kaydedilemedi.')
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong>{o.adSoyad}</strong>
        <span className="alt" style={{ margin: 0 }}>
          {OGRETMEN_TURU_ADI[o.tur]}
          {o.okul ? ` · ${o.okul}` : ''}
          {o.brans ? ` · ${o.brans}` : ''} · {OGRETMEN_ADRESI[o.tur]}.nirengi
        </span>
        <span className="alt" style={{ margin: 0, marginLeft: 'auto' }}>
          {gun(o.atandi)} tarihinde atandı
        </span>
      </div>

      {o.not && !notAcik && (
        <div className="bilgi" style={{ marginTop: 8 }}>
          <strong style={{ fontSize: 13 }}>Bu öğretmene yazdığınız not</strong>
          <div style={{ whiteSpace: 'pre-wrap' }}>{o.not}</div>
          <p className="alt" style={{ margin: '6px 0 0' }}>
            {gun(o.notTarih)}
          </p>
        </div>
      )}

      {notAcik && (
        <div style={{ marginTop: 8 }}>
          {hata && <div className="uyari">{hata}</div>}
          <label>
            {o.adSoyad} için not — <strong>öğretmen bunu görecek</strong>
            <textarea
              rows={5}
              value={not}
              onChange={(e) => setNot(e.target.value)}
              maxLength={4000}
              placeholder="Sınıfta ne denenmesini istiyorsunuz, nelere dikkat etmesi gerekiyor?"
            />
          </label>
          <p className="alt" style={{ marginTop: -6 }}>
            Klinik not değil: tanı, ölçüm puanı ve aile bilgisi buraya yazılmaz. Klinik
            kayıtlar seans notlarında duruyor ve okula açılmıyor.
          </p>
          <div className="dugmeler">
            <button className="birincil" onClick={() => void kaydet()} disabled={bekliyor}>
              {bekliyor ? 'Kaydediliyor…' : 'Notu kaydet'}
            </button>
            <button onClick={() => setNotAcik(false)} disabled={bekliyor}>
              Vazgeç
            </button>
          </div>
        </div>
      )}

      {yazabilir && !notAcik && (
        <div className="dugmeler">
          <button onClick={() => setNotAcik(true)}>{o.not ? 'Notu düzenle' : 'Not yaz'}</button>
          {/*
            HATA YAKALANIYOR. Eskiden `try/catch` yoktu ve `onDegisti`
            yalnızca başarıda koşuyordu: istek düşerse kullanıcı hiçbir
            uyarı görmüyor, satır listede duruyor ve atamanın
            kaldırılıp kaldırılmadığını bilemiyordu. Aynı bileşende not
            kaydetme zaten hatayı yakalayıp yazıyor.
          */}
          <button
            disabled={bekliyor}
            onClick={async () => {
              setHata(null)
              setBekliyor(true)
              try {
                await ogretmenApi.atamaKaldir(o.atamaId)
                onDegisti()
              } catch {
                setHata('Atama kaldırılamadı. Tekrar deneyin.')
              } finally {
                setBekliyor(false)
              }
            }}
          >
            Atamayı kaldır
          </button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Öğretmen seçimi.
 *
 * Önce merkezde KAYITLI öğretmenler aranıyor; aynı sınıftan ikinci
 * öğrenci geldiğinde yeni kayıt açmak değil var olanı bulmak gerekiyor.
 * Listede yoksa oradan yeni kayıt açılabiliyor.
 */
function OgretmenSec({
  danisanId,
  atanmis,
  onAtandi,
}: {
  danisanId: string
  atanmis: Set<string>
  onAtandi: () => void
}) {
  const [ara, setAra] = useState('')
  const [satirlar, setSatirlar] = useState<OgretmenSatiri[]>([])
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    let iptal = false
    ogretmenApi
      .liste(ara.trim() || undefined)
      .then((r) => {
        if (!iptal) setSatirlar(r.ogretmenler.filter((o) => !o.arsiv))
      })
      .catch(() => undefined)
    return () => {
      iptal = true
    }
  }, [ara])

  const ata = async (ogretmenId: string) => {
    setHata(null)
    try {
      await ogretmenApi.ata(ogretmenId, danisanId)
      onAtandi()
    } catch (x) {
      setHata(
        x instanceof ApiHatasi && x.kod === 'okul_onayi_yok'
          ? 'Okulla paylaşım onamı olmadan atama yapılamıyor.'
          : 'Atanamadı.',
      )
    }
  }

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}>
      {hata && <div className="uyari">{hata}</div>}
      <label>
        Kayıtlı öğretmenler arasında ara
        <input
          value={ara}
          onChange={(e) => setAra(e.target.value)}
          placeholder="Ad, okul veya e-posta"
          autoFocus
        />
      </label>

      {satirlar.length === 0 ? (
        <p className="alt">
          Kayıt bulunamadı. Yeni öğretmen eklemek için üst menüdeki <strong>Okul</strong>
          bölümünü kullanın; eklendikten sonra buradan atayabilirsiniz.
        </p>
      ) : (
        <table>
          <tbody>
            {satirlar.map((o) => (
              <tr key={o.id}>
                <td>
                  {o.adSoyad}
                  <span className="alt">
                    {' · '}
                    {OGRETMEN_TURU_ADI[o.tur]}
                    {o.okul ? ` · ${o.okul}` : ''}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {atanmis.has(o.id) ? (
                    <span className="alt">Atanmış</span>
                  ) : (
                    <button onClick={() => void ata(o.id)}>Ata</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
